import {
  CartesianGrid,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { ChartTooltipShell } from "../panel";
import { TIER_COLOR, TIER_ORDER, tierBands, tierForScore } from "../macro-tier-colors";
import { formatCompactCurrency } from "../../../pages/PortfolioPerformance/lib/format";
import type { IndustryPoint, MacroTier, SensitivityTier } from "../../../pages/PortfolioPerformance/lib/api/macro-exposure";

const AXIS_COLOR = "var(--neutral-inactive)";

// How far the y axis extends: past 100 by roughly one bubble radius.
const Y_AXIS_MAX = 115;

/** "Medium" in its band's colour (amber / red / green). */
function TierWord({ tier }: { tier: SensitivityTier }) {
  return (
    <span className="font-medium" style={{ color: TIER_COLOR[tier] }}>
      {tier}
    </span>
  );
}

/** "Sensitivity Medium (was High)": the band now, and the band last quarter when there is a previous score. */
function SensitivityLine({ p, tiers }: { p: IndustryPoint; tiers?: MacroTier[] }) {
  const was = p.change !== null ? tierForScore(p.sensitivity - p.change, tiers) : null;
  return (
    <p className="text-muted-foreground">
      Sensitivity <TierWord tier={p.tier} />
      {was ? (
        <>
          {" "}
          (was <TierWord tier={was} />)
        </>
      ) : null}
    </p>
  );
}

function PointTooltip({
  active,
  payload,
  variant,
  tiers,
}: {
  active?: boolean;
  payload?: Array<{ payload: IndustryPoint & { x: number } }>;
  variant: "industry" | "variable";
  tiers?: MacroTier[];
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0]!.payload;
  if (variant === "variable") {
    // A macro variable has no volume: its score and how it moved are what the bubble says.
    return (
      <ChartTooltipShell>
        <p className="font-semibold text-foreground">{p.name}</p>
        <SensitivityLine p={p} tiers={tiers} />
        {p.change !== null ? (
          <p className="text-muted-foreground">
            Change{" "}
            <span className="tabular-nums text-amber">
              {p.change > 0 ? "+" : ""}
              {p.change}
            </span>{" "}
            vs last quarter
          </p>
        ) : null}
        <p className="mt-1 text-foreground">Click for the last 4 quarters.</p>
      </ChartTooltipShell>
    );
  }
  return (
    <ChartTooltipShell>
      <p className="font-semibold text-foreground">{p.name}</p>
      <SensitivityLine p={p} tiers={tiers} />
      <p className="text-muted-foreground">
        Volume{" "}
        <span className="tabular-nums text-foreground">
          {p.volume > 0 ? formatCompactCurrency(p.volume) : "Not Available Yet."}
        </span>
      </p>
      <p className="mt-1 text-foreground">Click for Macro Indicator details</p>
    </ChartTooltipShell>
  );
}

export function SensitivityBubbleChart<T extends IndustryPoint = IndustryPoint>({
  points,
  onSelect,
  height = 320,
  variant = "industry",
  tiers,
}: {
  points: T[];
  onSelect: (point: T) => void;
  height?: number;
  /** "variable" is the macro variable chart: longer axis labels and a tooltip about change, not volume. */
  variant?: "industry" | "variable";
  /** The sensitivity bands (Low / Medium / High and where each starts). With them the y axis is labelled with the
   *  band names instead of 0-100 scores; the bubbles still sit at their score. */
  tiers?: MacroTier[];
}) {
  // Bands, lowest first: where each starts and ends on the 0-100 scale, and the middle that carries its axis label.
  const bands = tierBands(tiers, Y_AXIS_MAX);
  const labelLength = variant === "variable" ? 16 : 8;
  // With no volume for any industry, draw equal bubbles instead of a row of dots at the minimum size.
  const noVolumes = points.every((p) => !(p.volume > 0));
  const withX = points.map((p, i) => ({ ...p, x: i, size: noVolumes ? 1 : p.volume }));
  const byTier = TIER_ORDER.map((tier) => ({
    tier,
    data: withX.filter((p) => p.tier === tier),
  }));

  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 12, right: 16, bottom: 24, left: 8 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
          <XAxis
            type="number"
            dataKey="x"
            domain={[-1, points.length]}
            ticks={withX.map((p) => p.x)}
            tickFormatter={(v: number) => withX[v]?.name.slice(0, labelLength) ?? ""}
            tick={{ fill: AXIS_COLOR, fontSize: 9 }}
            stroke={AXIS_COLOR}
            angle={-30}
            textAnchor="end"
            height={50}
          />
          <YAxis
            type="number"
            dataKey="sensitivity"
            // The scale is 0 to 100, but the axis runs a little past it so a bubble AT 100 (the strongest macro
            // variable always is) is drawn whole instead of being cut by the top edge. The labels stay 0..100.
            domain={[0, Y_AXIS_MAX]}
            ticks={bands.length ? bands.map((b) => b.mid) : [0, 25, 50, 75, 100]}
            tickFormatter={(v: number) => bands.find((b) => b.mid === v)?.tier ?? String(v)}
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            tickLine={false}
            stroke={AXIS_COLOR}
            width={bands.length ? 64 : 40}
            label={{
              value: "Sensitivity",
              angle: -90,
              position: "insideLeft",
              fill: AXIS_COLOR,
              fontSize: 11,
            }}
          />
          {bands.map((b) => (
            <ReferenceArea key={`area-${b.tier}`} y1={b.from} y2={b.to} fill={TIER_COLOR[b.tier]} fillOpacity={0.05} />
          ))}
          {bands.slice(1).map((b) => (
            <ReferenceLine key={b.tier} y={b.from} stroke="var(--border)" strokeDasharray="2 4" />
          ))}
          <ZAxis type="number" dataKey="size" range={[80, 900]} />
          <Tooltip content={<PointTooltip variant={variant} tiers={tiers} />} />
          {byTier.map(({ tier, data }) => (
            <Scatter
              key={tier}
              name={tier}
              data={data}
              fill={TIER_COLOR[tier]}
              fillOpacity={0.6}
              stroke={TIER_COLOR[tier]}
              isAnimationActive={false}
              cursor="pointer"
              onClick={(entry: unknown) => {
                const point = (entry as { payload?: T })?.payload;
                if (point) onSelect(point);
              }}
            />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}
